import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import GlobalFooter from '../components/GlobalFooter';
import SeoHead from '../components/SeoHead';
// PromptBar import removed per Miles 2026-04-06

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const HomeHeader = styled.header`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderIconLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }

  &.active {
    color: var(--brand-gold);
    background-color: rgba(198, 167, 94, 0.08);

    svg {
      opacity: 1;
      color: var(--brand-gold);
    }
  }
`;

const HeaderIconButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--brand-line);
  border-radius: 10px;
  z-index: 1100;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const MobileMenuDropdown = styled.div<{ $open: boolean }>`
  display: none;
  position: fixed;
  top: 4.25rem;
  right: 1rem;
  width: min(260px, calc(100vw - 1.5rem));
  background: #fff;
  border: 1px solid var(--brand-line);
  border-radius: 12px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
  padding: 0.5rem 0;
  z-index: 1200;
  flex-direction: column;
  opacity: ${props => props.$open ? 1 : 0};
  transform: ${props => props.$open ? 'translateY(0)' : 'translateY(-8px)'};
  pointer-events: ${props => props.$open ? 'auto' : 'none'};
  transition: opacity 0.2s ease, transform 0.2s ease;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  min-height: 44px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: var(--brand-text);
  }
`;

const MobileMenuButton = styled.button`
  color: var(--brand-text-secondary);
  background: transparent;
  border: none;
  width: 100%;
  min-height: 44px;
  padding: 0.75rem 1rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: var(--brand-text);
  }
`;

const PageContainer = styled.div`
  padding: 6rem 2.5rem 0;
  padding-bottom: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--brand-canvas);

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 0;
  }
`;

const PrivacyHeader = styled.div`
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-charcoal) 100%);
  color: white;
  padding: 3rem clamp(2rem, 6vw, 4rem);
  border-radius: 20px;
  margin-bottom: clamp(3rem, 7vw, 4rem);
  position: relative;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(26, 26, 26, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.07' fill-rule='evenodd'/%3E%3C/svg%3E") repeat;
    opacity: 0.3;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 70%;
    background: linear-gradient(to top, rgba(26, 26, 26, 0.2) 0%, transparent 100%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 2.5rem 1.75rem;
    border-radius: 16px;
  }
`;

const PrivacyTitle = styled.h1`
  font-size: clamp(2.2rem, 4vw, 2.75rem);
  font-weight: 800;
  margin-bottom: 1.25rem;
  line-height: 1.15;
  position: relative;
  z-index: 2;
  letter-spacing: -0.02em;
  color: var(--brand-canvas);
  text-align: center;
  font-family: 'Space Grotesk', sans-serif;
`;

const ContentContainer = styled.div`
  max-width: 1024px;
  margin: 0 auto clamp(4rem, 8vw, 5rem);
  width: 100%;
`;

const SummaryCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: clamp(1.75rem, 4vw, 2.5rem);
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--brand-line);
  margin-bottom: clamp(2.5rem, 6vw, 3.5rem);
  animation: ${fadeIn} 0.9s ease-out;
`;

const SummaryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  letter-spacing: 0.06em;
  color: var(--brand-gold);
  background: rgba(198, 167, 94, 0.1);
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  margin-bottom: 1.25rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const SummaryHeading = styled.h2`
  font-size: clamp(1.6rem, 3.2vw, 2rem);
  color: var(--brand-black);
  margin-bottom: 1.5rem;
  font-weight: 700;
  text-transform: lowercase;
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const SummaryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem 2rem;
`;

const SummaryItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: var(--brand-text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &::before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-top: 0.4rem;
    background: linear-gradient(135deg, var(--brand-gold), var(--brand-gold-hover));
    flex: 0 0 auto;
  }
`;

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 280px) minmax(0, 1fr);
  gap: clamp(2rem, 5vw, 3.5rem);
  align-items: flex-start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SectionNav = styled.nav`
  position: sticky;
  top: 120px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border-radius: 18px;
  border: 1px solid var(--brand-line);
  padding: 1.5rem;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.05);

  @media (max-width: 1024px) {
    position: static;
    box-shadow: none;
    border-radius: 16px;
    backdrop-filter: none;
  }
`;

const SectionNavTitle = styled.p`
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: lowercase;
  color: var(--brand-text-secondary);
  margin-bottom: 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const SectionNavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const SectionNavItem = styled.li`
  width: 100%;
`;

const SectionNavLink = styled.a<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-decoration: none;
  font-size: 0.9rem;
  color: var(--brand-text-secondary);
  padding: 0.75rem 0.85rem;
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  ${({ $active }) => $active && css`
    background: rgba(198, 167, 94, 0.12);
    color: var(--brand-gold);
    font-weight: 600;
  `}

  &:hover {
    background: rgba(198, 167, 94, 0.08);
    color: var(--brand-gold);
  }
`;

const SectionWrapper = styled.section`
  margin-bottom: clamp(2.5rem, 6vw, 3.5rem);
  scroll-margin-top: 140px;

  @media (max-width: 768px) {
    scroll-margin-top: 100px;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2rem);
  color: var(--brand-black);
  margin-bottom: 1.5rem;
  text-transform: lowercase;
  animation: ${fadeInLeft} 0.6s ease-out;
  position: relative;
  display: inline-block;
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 700;
  letter-spacing: -0.01em;
  padding-bottom: 0.75rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, var(--brand-gold), var(--brand-gold-hover));
    border-radius: 2px;
  }
`;

const SubSectionTitle = styled.h3`
  font-size: clamp(1.2rem, 2.2vw, 1.35rem);
  color: var(--brand-charcoal);
  margin-bottom: 0.75rem;
  margin-top: 1.75rem;
  text-transform: lowercase;
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
`;

const PrivacyText = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: var(--brand-text-secondary);
  margin-bottom: 1.25rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    font-size: 0.975rem;
  }
`;

const PrivacyList = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
  display: grid;
  gap: 0.75rem;
`;

const PrivacyListItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const CalloutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin: 2rem 0 1.5rem;
`;

const CalloutCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--brand-line);
  padding: 1.25rem;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.05);
`;

const CalloutLabel = styled.span`
  display: block;
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  color: var(--brand-text-secondary);
  text-transform: lowercase;
  margin-bottom: 0.6rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const CalloutValue = styled.span`
  display: block;
  font-size: 1.05rem;
  color: var(--brand-black);
  font-weight: 600;
  margin-bottom: 0.35rem;
  word-break: break-word;
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const CalloutNote = styled.span`
  display: block;
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const InfoBlock = styled.div`
  background: linear-gradient(135deg, rgba(198, 167, 94, 0.08), rgba(26, 26, 26, 0.04));
  border: 1px solid rgba(198, 167, 94, 0.2);
  border-radius: 18px;
  padding: clamp(1.25rem, 3vw, 1.75rem);
  margin: clamp(1.75rem, 4vw, 2.5rem) 0;
  color: var(--brand-black);
`;

const InlineLink = styled(Link)`
  color: var(--brand-gold);
  text-decoration: none;
  border-bottom: 1px solid rgba(198, 167, 94, 0.5);
  padding-bottom: 1px;
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover {
    color: var(--brand-gold-hover);
    border-bottom-color: rgba(182, 146, 76, 0.7);
  }
`;

const FooterWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;

type Section = {
  id: string;
  title: string;
  content: React.ReactNode;
};

const Privacy: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const timeoutIds = [
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 100),
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 300),
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 500),
    ];

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, []);

  const scrollToLogin = () => {
    navigate('/');
    setTimeout(() => {
      const loginSection = document.getElementById('login-section');
      loginSection?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const privacyStructuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'PrivacyPolicy',
    name: 'Privacy Policy | prest0',
    description: 'prest0 explains how personal data is collected, used, and protected across the AI agent platform.',
    url: 'https://prest0.ai/privacy-policy',
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://prest0.ai/privacy-policy',
    },
    publisher: {
      '@type': 'Organization',
      name: 'prest0',
      url: 'https://prest0.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.prest0.ai/images/prest0-logo.webp',
      },
    },
  }), []);

  const sections = useMemo<Section[]>(
    () => [
      {
        id: 'overview',
        title: 'overview',
        content: (
          <>
            <PrivacyText>
              prest0 ("prest0", "we", "our", or "us") builds ai agent workflows that help teams ship automations without code. This privacy policy explains how we collect, use, store, and share personal information for visitors, builders, investors, and partners who interact with the live experience at prest0.ai and any related services that link to this notice (collectively, the "services").
            </PrivacyText>
            <PrivacyText>
              By using the services, you agree to the practices described here as well as in our <InlineLink to="/terms-of-service">terms of service</InlineLink> and <InlineLink to="/cookie-policy">cookie policy</InlineLink>. If you disagree with this policy, please discontinue use of the services.
            </PrivacyText>
            <PrivacyText>
              The privacy commitments below match what we publish on the security page: TLS 1.3 for ingress, AES-256 encryption at rest on google cloud, enterprise OAuth 2.0 with multi-factor authentication, configurable 30/60/90-day retention, and a contractual no-training guarantee for every prompt handled by enterprise AI endpoints.
            </PrivacyText>
            <CalloutGrid>
              <CalloutCard>
                <CalloutLabel>data controller</CalloutLabel>
                <CalloutValue>prest0</CalloutValue>
                <CalloutNote>applies to every workspace</CalloutNote>
              </CalloutCard>
              <CalloutCard>
                <CalloutLabel>security baseline</CalloutLabel>
                <CalloutValue>tls 1.3 + aes-256</CalloutValue>
                <CalloutNote>matches the controls in our security page</CalloutNote>
              </CalloutCard>
              <CalloutCard>
                <CalloutLabel>retention controls</CalloutLabel>
                <CalloutValue>30/60/90-day defaults</CalloutValue>
                <CalloutNote>custom schedules supported per workspace</CalloutNote>
              </CalloutCard>
            </CalloutGrid>
            <PrivacyText>
              This policy applies whether you access prest0 directly, participate in private beta programs, engage with us through live demonstrations, or communicate with our team via chat, email, or social channels.
            </PrivacyText>
          </>
        ),
      },
      {
        id: 'information-we-collect',
        title: 'information we collect',
        content: (
          <>
            <SubSectionTitle>information you provide to us</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>account details such as name, email address, company, and authentication credentials when you create or manage a prest0 account.</PrivacyListItem>
              <PrivacyListItem>profile preferences, notification settings, and any configuration data you save inside your workspace.</PrivacyListItem>
              <PrivacyListItem>support requests, survey responses, chat conversations, and other communications with our team.</PrivacyListItem>
              <PrivacyListItem>billing contact data and limited payment-related metadata when you engage in paid features (payment details are stored securely by our payment processor).</PrivacyListItem>
            </PrivacyList>
            <SubSectionTitle>information collected automatically</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>log data such as browser type, device information, operating system, and ip address used to access the services.</PrivacyListItem>
              <PrivacyListItem>usage analytics including pages viewed, features clicked, session duration, and referring pages to help improve product performance.</PrivacyListItem>
              <PrivacyListItem>cookie identifiers and similar technologies used to remember preferences, maintain session security, and (with consent) measure engagement.</PrivacyListItem>
              <PrivacyListItem>diagnostic data, crash reports, and trace logs generated automatically by our infrastructure providers.</PrivacyListItem>
            </PrivacyList>
            <SubSectionTitle>information from third parties</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>authentication providers (such as google) that share tokens or profile basics when you opt in to single sign-on.</PrivacyListItem>
              <PrivacyListItem>payment processors (stripe) that confirm billing status, card brand, and the last four digits of payment methods.</PrivacyListItem>
              <PrivacyListItem>service partners that help deliver functionality (for example, cloud hosting or ai model providers) where permitted by their policies and yours.</PrivacyListItem>
            </PrivacyList>
            <SubSectionTitle>information generated by ai agents</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>prompt content and agent outputs that you explicitly save to your workspace.</PrivacyListItem>
              <PrivacyListItem>system telemetry that helps us monitor performance, latency, and error recovery for automated workflows.</PrivacyListItem>
              <PrivacyListItem>anonymized or aggregated insights derived from agent runs that cannot be traced back to an individual user.</PrivacyListItem>
            </PrivacyList>
            <SubSectionTitle>information collected via sms/text messaging</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>phone number you provide when opting in to receive text messages from prest0.</PrivacyListItem>
              <PrivacyListItem>message content, delivery status, and opt-in/opt-out preferences associated with your mobile number.</PrivacyListItem>
              <PrivacyListItem>carrier information used for message routing and delivery confirmation.</PrivacyListItem>
            </PrivacyList>
          </>
        ),
      },
      {
        id: 'how-we-use-information',
        title: 'how we use information',
        content: (
          <>
            <PrivacyText>
              We process personal information so we can operate a secure, reliable, and continuously improving ai agent experience. Specifically, we use data to:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>deliver core product functionality, including authentication, workspace management, collaboration, and agent execution.</PrivacyListItem>
              <PrivacyListItem>support you with onboarding, documentation, success guidance, and customer care.</PrivacyListItem>
              <PrivacyListItem>maintain platform health, detect anomalies, prevent fraud, and troubleshoot issues.</PrivacyListItem>
              <PrivacyListItem>research and build new capabilities, including personalization, automation improvements, and analytics dashboards.</PrivacyListItem>
              <PrivacyListItem>communicate service updates, security alerts, or administrative announcements.</PrivacyListItem>
              <PrivacyListItem>send transactional and informational text messages to your mobile phone when you opt in, such as case updates, appointment reminders, and account notifications.</PrivacyListItem>
              <PrivacyListItem>comply with legal obligations, enforce agreements, and protect the rights of prest0 and our users.</PrivacyListItem>
            </PrivacyList>
            <InfoBlock>
              <PrivacyText style={{ marginBottom: 0 }}>
                We do not sell personal information. We only use data to support the services you request, and we honor your consent choices for analytics and marketing.
              </PrivacyText>
            </InfoBlock>
          </>
        ),
      },
      {
        id: 'legal-bases',
        title: 'legal bases for processing',
        content: (
          <>
            <PrivacyText>
              When required under applicable law (including the gdpr and comparable regulations), we rely on these legal bases:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>performance of a contract when supplying requested services, maintaining your account, or providing support.</PrivacyListItem>
              <PrivacyListItem>legitimate interests to secure our infrastructure, improve features, and understand product adoption in a privacy-aware manner.</PrivacyListItem>
              <PrivacyListItem>compliance with legal obligations, such as tax, accounting, or regulatory requirements.</PrivacyListItem>
              <PrivacyListItem>consent for optional analytics, marketing communications, beta programs, or data processing that is not strictly necessary.</PrivacyListItem>
              <PrivacyListItem>protection of vital interests when responding to urgent safety matters or preventing harmful activity.</PrivacyListItem>
            </PrivacyList>
          </>
        ),
      },
      {
        id: 'information-sharing',
        title: 'how we share information',
        content: (
          <>
            <PrivacyText>
              We share personal information only with trusted parties that help us operate or when you direct us to do so:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>infrastructure and product vendors (google cloud / firebase, auth providers, customer support tooling) that follow strict data protection terms.</PrivacyListItem>
              <PrivacyListItem>payment partners (stripe) that securely manage billing and financial transactions.</PrivacyListItem>
              <PrivacyListItem>enterprise AI execution partners that run prompts through our policy proxy. These vendors must honor contractual no-training guarantees before any data is routed their way.</PrivacyListItem>
              <PrivacyListItem>legal authorities when required by law, court order, or to defend our rights.</PrivacyListItem>
              <PrivacyListItem>future corporate transactions, such as a merger or acquisition, where your information remains protected by this policy or a successor policy with equal safeguards.</PrivacyListItem>
            </PrivacyList>
            <InfoBlock>
              <PrivacyText style={{ marginBottom: 0 }}>
                We do not permit partners to use your personal information for their own independent marketing. Every vendor agreement includes confidentiality and security commitments.
              </PrivacyText>
            </InfoBlock>
          </>
        ),
      },
      {
        id: 'data-retention',
        title: 'data retention',
        content: (
          <>
            <PrivacyText>
              We retain personal information only for as long as it is needed to deliver the services, meet legal requirements, or resolve disputes. Default retention windows match the security program (30/60/90-day options), and you can apply custom SLAs per workspace. When data is no longer required, we delete or anonymize it in line with documented schedules.
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>account data persists while your workspace remains active and for a short grace period if you deactivate the account.</PrivacyListItem>
              <PrivacyListItem>agent prompts, transcripts, and attachments inherit the retention timer you set (30/60/90-day presets or custom intervals) and produce deletion receipts once the window lapses.</PrivacyListItem>
              <PrivacyListItem>support tickets and chat transcripts are retained for quality assurance and legal compliance, typically no longer than 24 months.</PrivacyListItem>
              <PrivacyListItem>aggregated analytics that cannot be tied to an individual may be kept to inform long-term product strategy.</PrivacyListItem>
              <PrivacyListItem>backups containing personal data are cycled on fixed schedules and purged using secure deletion procedures.</PrivacyListItem>
            </PrivacyList>
            <PrivacyText>
              You may request deletion of your personal data at any time. We respond to verified deletion requests within 30 days unless applicable law requires us to retain certain records.
            </PrivacyText>
          </>
        ),
      },
      {
        id: 'sms-text-messaging',
        title: 'sms & text messaging',
        content: (
          <>
            {/* PRE-72: /signup links to /privacy-policy#sms. We keep the existing
                section id ('sms-text-messaging') so the in-page TOC and scroll-spy
                keep working, and expose an additional 'sms' anchor inside the
                section body so the shorter URL still scrolls here. */}
            <span id="sms" style={{ display: 'block', position: 'relative', top: '-120px' }} aria-hidden="true" />
            <PrivacyText>
              prest0 uses Twilio to send SMS text messages from (628) 444-4018. By providing your phone number and opting in, you consent to receive text messages from prest0 at the number provided. The following disclosures apply to all SMS communications:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>message frequency varies based on your account activity and notification preferences.</PrivacyListItem>
              <PrivacyListItem>message and data rates may apply. Check with your mobile carrier for applicable rates.</PrivacyListItem>
              <PrivacyListItem>you can opt out at any time by replying STOP to any message. You will receive one final confirmation message and then no further texts.</PrivacyListItem>
              <PrivacyListItem>for help, reply HELP to any message or contact us at <a href="mailto:support@prest0.ai" style={{ color: 'var(--brand-gold)', textDecoration: 'none', borderBottom: '1px solid rgba(198, 167, 94, 0.5)', paddingBottom: '1px' }}>support@prest0.ai</a>.</PrivacyListItem>
              <PrivacyListItem>we do not share your phone number with third parties for their own marketing purposes.</PrivacyListItem>
              <PrivacyListItem>consent to receive text messages is not a condition of purchase or use of any prest0 service.</PrivacyListItem>
              <PrivacyListItem>supported carriers include major US carriers. Carrier message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</PrivacyListItem>
            </PrivacyList>
            <InfoBlock>
              <PrivacyText style={{ marginBottom: 0 }}>
                Text messages from prest0 are transactional and informational in nature—case updates, appointment reminders, and account notifications. We do not send unsolicited marketing texts. Your opt-in consent can be withdrawn at any time by replying STOP.
              </PrivacyText>
            </InfoBlock>

            {/* PRE-72: carrier-compliance-grade SMS disclosures, additive to the
                existing copy above. Twilio A2P reviewers specifically asked us
                to spell out (i) what SMS-specific data we collect, (ii) why,
                (iii) who we share with, (iv) opt-out paths, and (v) retention. */}
            <SubSectionTitle>what sms data we collect</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>phone number (normalized to E.164) you provide at opt-in.</PrivacyListItem>
              <PrivacyListItem>consent timestamp recorded by our servers (UTC).</PrivacyListItem>
              <PrivacyListItem>IP address captured from your network request at the time of opt-in.</PrivacyListItem>
              <PrivacyListItem>user-agent string from the browser or device used to opt in.</PrivacyListItem>
              <PrivacyListItem>double-opt-in confirmation status (pending / confirmed / opted-out / expired).</PrivacyListItem>
            </PrivacyList>
            <SubSectionTitle>why we collect it</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>carrier-compliance audit trails for A2P 10DLC, TCPA, and similar regulations.</PrivacyListItem>
              <PrivacyListItem>routing and delivery of account notifications you explicitly requested.</PrivacyListItem>
              <PrivacyListItem>fraud prevention and abuse detection (e.g. catching bulk opt-in abuse).</PrivacyListItem>
            </PrivacyList>
            <SubSectionTitle>who we share your phone number with</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>Twilio (SMS delivery processor under a signed DPA) and mobile network operators in the delivery path.</PrivacyListItem>
              <PrivacyListItem>nobody else. We do not share or sell your phone number to third-party marketers, data brokers, or advertisers.</PrivacyListItem>
            </PrivacyList>
            <SubSectionTitle>how to opt out</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>text STOP (or STOPALL / UNSUBSCRIBE / CANCEL / END / QUIT) to any Prest0n message.</PrivacyListItem>
              <PrivacyListItem>toggle SMS off in your account settings at any time.</PrivacyListItem>
              <PrivacyListItem>email <a href="mailto:support@prest0.ai" style={{ color: 'var(--brand-gold)', textDecoration: 'none', borderBottom: '1px solid rgba(198, 167, 94, 0.5)', paddingBottom: '1px' }}>support@prest0.ai</a> to request manual removal.</PrivacyListItem>
            </PrivacyList>
            <SubSectionTitle>retention of sms data</SubSectionTitle>
            <PrivacyList>
              <PrivacyListItem>your phone number is removed from active send lists within 24 hours of opt-out.</PrivacyListItem>
              <PrivacyListItem>the consent audit record (timestamp, IP, UA, opt-in/opt-out events) is retained for as long as carrier-compliance regulations require it. See the dedicated <InlineLink to="/sms-terms">SMS Terms</InlineLink> page for the full program description.</PrivacyListItem>
            </PrivacyList>
          </>
        ),
      },
      {
        id: 'data-security',
        title: 'data security',
        content: (
          <>
            <PrivacyText>
              Keeping your data safe is fundamental to our platform design. We implement the same layered safeguards described on the security page, including:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>TLS 1.3 with hsts locks down every session from browser to api, and we disable legacy protocols.</PrivacyListItem>
              <PrivacyListItem>data stored in google cloud firestore and storage is encrypted with aes-256 plus managed key rotation.</PrivacyListItem>
              <PrivacyListItem>oauth 2.0 with adaptive mfa, device posture checks, and scoped api keys enforces least-privilege access.</PrivacyListItem>
              <PrivacyListItem>immutable audit logs, automated alerts, and 48-hour incident response SLAs provide verifiable oversight.</PrivacyListItem>
              <PrivacyListItem>enterprise ai endpoints run under contractual no-training guarantees so prompts never fine-tune shared models.</PrivacyListItem>
              <PrivacyListItem>we inherit soc 2 type ii and iso/iec 27001 controls from google cloud and layer ongoing penetration tests on top.</PrivacyListItem>
            </PrivacyList>
          </>
        ),
      },
      {
        id: 'ai-automation',
        title: 'ai agents & automation',
        content: (
          <>
            <PrivacyText>
              prest0 offers ai-powered automation. We apply guardrails to ensure responsible use of data within these workflows:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>agents operate inside isolated sandboxes with per-field data protections and debounced persistence to avoid overwriting active edits.</PrivacyListItem>
              <PrivacyListItem>we log the minimum metadata necessary to debug agent performance while respecting user privacy preferences.</PrivacyListItem>
              <PrivacyListItem>when you connect external data sources, we process the information strictly according to your documented instructions.</PrivacyListItem>
              <PrivacyListItem>automated decisions that may significantly affect users always include human review and override options.</PrivacyListItem>
              <PrivacyListItem>no-training guarantees mean prompts, transcripts, and attachments never fine-tune shared models; they remain inside governed enterprise endpoints.</PrivacyListItem>
            </PrivacyList>
          </>
        ),
      },
      {
        id: 'international-transfers',
        title: 'international transfers',
        content: (
          <>
            <PrivacyText>
              Our primary infrastructure runs on google cloud and firebase regions selected for resiliency. When you access the services, data may move between those regions so we can deliver the experience with low latency.
            </PrivacyText>
            <PrivacyText>
              We rely on approved transfer mechanisms—such as standard contractual clauses and vendor-specific safeguards—to protect personal data whenever it crosses borders, and we monitor regulatory updates to remain compliant.
            </PrivacyText>
          </>
        ),
      },
      {
        id: 'privacy-choices',
        title: 'your privacy choices',
        content: (
          <>
            <PrivacyText>
              You remain in control of your personal information. Depending on your jurisdiction, you may have the right to:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>access, review, or export a copy of the data we maintain about you.</PrivacyListItem>
              <PrivacyListItem>update or correct inaccurate information via account settings or by contacting support.</PrivacyListItem>
              <PrivacyListItem>request deletion or anonymization of your personal data.</PrivacyListItem>
              <PrivacyListItem>opt out of non-essential communications and withdraw consent for analytics at any time.</PrivacyListItem>
              <PrivacyListItem>restrict or object to certain processing activities where legally permitted.</PrivacyListItem>
            </PrivacyList>
            <InfoBlock>
              <PrivacyText style={{ marginBottom: 0 }}>
                Submit privacy requests through the in-product workspace settings or live chat. We verify each request inside the app, then keep an auditable record so you can confirm completion.
              </PrivacyText>
            </InfoBlock>
          </>
        ),
      },
      {
        id: 'cookies',
        title: 'cookies & tracking technologies',
        content: (
          <>
            <PrivacyText>
              We use cookies, local storage, and similar technologies to keep you signed in, remember preferences, and (with your consent) measure performance. You can manage consent through the privacy banner and adjust browser settings to refuse or delete cookies.
            </PrivacyText>
            <PrivacyText>
              For a detailed breakdown of cookie categories, retention windows, and opt-out controls, visit our dedicated <InlineLink to="/cookie-policy">cookie policy</InlineLink>.
            </PrivacyText>
          </>
        ),
      },
      {
        id: 'third-parties',
        title: 'third-party services & integrations',
        content: (
          <>
            <PrivacyText>
              To deliver the services, we partner with carefully vetted providers. Key partners include:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>google cloud / firebase for secure hosting, authentication, firestore database, and storage.</PrivacyListItem>
              <PrivacyListItem>stripe for payment processing and financial reporting.</PrivacyListItem>
              <PrivacyListItem>enterprise AI endpoints that execute prompts with strict no-training guarantees and signed DPAs.</PrivacyListItem>
              <PrivacyListItem>operations tooling (support systems, logging, and analytics) that operate under strict confidentiality clauses.</PrivacyListItem>
            </PrivacyList>
            <PrivacyText>
              Each provider is required to implement industry-standard security controls and to use your data only as instructed by prest0.
            </PrivacyText>
          </>
        ),
      },
      {
        id: 'policy-updates',
        title: 'updates to this policy',
        content: (
          <>
            <PrivacyText>
              We review this privacy policy regularly to reflect new features, regulatory changes, or operational updates. When we make material changes, we will post a notice on the services, update the effective date above, and provide additional communication if required by law.
            </PrivacyText>
          </>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !(window as any).IntersectionObserver) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => {
            const aTop = (a.target as HTMLElement).offsetTop;
            const bTop = (b.target as HTMLElement).offsetTop;
            return aTop - bTop;
          });

        if (visible.length > 0) {
          const currentId = visible[0].target.getAttribute('id');
          if (currentId) {
            setActiveSection(currentId);
          }
        }
      },
      {
        rootMargin: '-50% 0px -45% 0px',
        threshold: [0.1, 0.25, 0.5],
      }
    );

    sections.forEach((section) => {
      const element = sectionRefs.current[section.id];
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [sections]);

  const handleSectionNavClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const element = sectionRefs.current[id];
    if (!element) return;

    const headerOffset = window.innerWidth > 1024 ? 110 : 80;
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!menuOpen) return;
      const target = event.target as Node;
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  return (
    <>
    <PageContainer>
      <SeoHead
        title="Privacy Policy | prest0"
        tabTitle="privacy policy"
        description="prest0's privacy policy: how we protect data for enterprise law firms and individual users on our legal AI platform."
        keywords="privacy policy, prest0, enterprise legal AI privacy, law firm data protection, legal AI data security"
        canonicalUrl="https://prest0.ai/privacy-policy"
        schemaType="WebPage"
        structuredData={privacyStructuredData}
      />
      <HomeHeader>
        <HeaderIconLink to="/team">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          team
        </HeaderIconLink>
        <HeaderIconLink to="/mission">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          mission
        </HeaderIconLink>
        <HeaderIconLink to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          home
        </HeaderIconLink>
        <HeaderIconButton onClick={scrollToLogin}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <polyline points="10 17 15 12 10 7" />
            <line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          login
        </HeaderIconButton>
      </HomeHeader>

      <MobileMenuToggle aria-label="Toggle menu" onClick={() => setMenuOpen((prev) => !prev)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {menuOpen ? (
            <path d="M18 6L6 18M6 6l12 12" />
          ) : (
            <>
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
      </MobileMenuToggle>

      <MobileMenuDropdown $open={menuOpen} ref={mobileMenuRef}>
        <MobileMenuLink to="/team" onClick={() => setMenuOpen(false)}>team</MobileMenuLink>
        <MobileMenuLink to="/mission" onClick={() => setMenuOpen(false)}>mission</MobileMenuLink>
        <MobileMenuLink to="/" onClick={() => setMenuOpen(false)}>home</MobileMenuLink>
        <MobileMenuButton onClick={() => { setMenuOpen(false); scrollToLogin(); }}>login</MobileMenuButton>
      </MobileMenuDropdown>

      <PrivacyHeader>
        <PrivacyTitle>privacy policy</PrivacyTitle>
      </PrivacyHeader>

      <ContentContainer>
        <SummaryCard>
          <SummaryBadge>key takeaways</SummaryBadge>
          <SummaryHeading>we protect your data so you can build ai agents with confidence</SummaryHeading>
          <SummaryList>
            <SummaryItem>no selling or renting of personal information—ever.</SummaryItem>
            <SummaryItem>tls 1.3, aes-256, oauth 2.0, and gcp soc 2 type ii controls secure every workspace.</SummaryItem>
            <SummaryItem>prompts, transcripts, and attachments never train shared models.</SummaryItem>
            <SummaryItem>simple paths to configure retention, access, export, or delete your data at any time.</SummaryItem>
          </SummaryList>
        </SummaryCard>

        <ContentLayout>
          <SectionNav aria-label="privacy policy table of contents">
            <SectionNavTitle>on this page</SectionNavTitle>
            <SectionNavList>
              {sections.map((section) => (
                <SectionNavItem key={section.id}>
                  <SectionNavLink
                    href={`#${section.id}`}
                    $active={activeSection === section.id}
                    onClick={(event) => handleSectionNavClick(event, section.id)}
                  >
                    {section.title}
                  </SectionNavLink>
                </SectionNavItem>
              ))}
            </SectionNavList>
          </SectionNav>

          <div>
            {sections.map((section) => (
              <SectionWrapper
                key={section.id}
                id={section.id}
                ref={(element) => {
                  sectionRefs.current[section.id] = element;
                }}
              >
                <SectionTitle>{section.title}</SectionTitle>
                {section.content}
              </SectionWrapper>
            ))}
          </div>
        </ContentLayout>
      </ContentContainer>

      <FooterWrapper>
        <GlobalFooter />
      </FooterWrapper>
    </PageContainer>
    {/* PromptBar removed — GlobalChatSystem will be added later */}
    </>
  );
};

export default Privacy;
